import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  FileCheck2, ChevronRight, ChevronLeft, RefreshCw, ShieldAlert,
  UserCheck, AlertTriangle, XCircle, Undo2, Search, PlayCircle,
} from "lucide-react";

const STATUS_CHIP = {
  UNPROCESSED:   "bg-[#121815] text-[#F8F9FA] border-[#3A4640]",
  IN_PROGRESS:   "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
  PROCESSED:     "bg-emerald-500/15 text-[#39FF14] border-emerald-500/30",
  NEEDS_REVIEW:  "bg-orange-500/15 text-orange-200 border-orange-500/30",
  INVALID:       "bg-red-500/15 text-red-200 border-red-500/30",
  COMPLETING:    "bg-blue-500/15 text-blue-200 border-blue-500/30",
};

const TAB_STATUS = {
  unprocessed:  "UNPROCESSED",
  mine:         "IN_PROGRESS",
  completed:    "PROCESSED",
  review:       "NEEDS_REVIEW",
  invalid:      "INVALID",
  all:          "",
};

// ============================================================
// Queue landing page — /admin/historical-processing
// ============================================================
export default function HistoricalProcessing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("unprocessed");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ district: "", block: "", gram_panchayat: "", village: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ counts: {}, workload: [] });
  const limit = 50;

  const canReview = user?.role === "admin" || (user?.permissions || []).includes("historical.review");
  const canView = !!user && (user.role === "admin" || (user.permissions || []).some(p => p.startsWith("historical.")));

  async function refresh() {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (TAB_STATUS[tab]) params.status = TAB_STATUS[tab];
      if (tab === "mine") params.assigned_to = "me";
      if (q) params.q = q;
      if (filters.district) params.district = filters.district;
      if (filters.block) params.block = filters.block;
      if (filters.gram_panchayat) params.gram_panchayat = filters.gram_panchayat;
      if (filters.village) params.village = filters.village;
      const [{ data }, statsRes] = await Promise.all([
        api.get("/historical-customers", { params }),
        api.get("/historical-customers/queue-stats"),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(statsRes.data || { counts: {}, workload: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setOffset(0); }, [tab, q, filters]);
  useEffect(() => { if (canView) refresh(); /* eslint-disable-line */ }, [tab, offset, filters, canView]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6 md:p-8" data-testid="historical-processing-landing">
      <PageHeader
        title="Historical Customer Processing"
        subtitle="Staff review historical records before they become customers with a TZAT Serial No."
        icon={FileCheck2}
        right={
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="hp-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        }
      />

      {/* Business-rule banner */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 mb-6 text-sm text-yellow-200 flex items-start gap-2">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <div>
          <b>IMPORT ≠ PROCESSING.</b> Uploaded historical rows land here as{" "}
          <b>UNPROCESSED</b>. They do <b>not</b> receive a TZAT Serial No. until a
          staff member successfully completes processing.
        </div>
      </div>

      {/* Queue stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ["Unprocessed",  stats.counts?.unprocessed,   "text-[#F8F9FA]"],
          ["In Progress",  stats.counts?.in_progress,   "text-yellow-200"],
          ["Processed",    stats.counts?.processed,     "text-[#39FF14]"],
          ["Needs Review", stats.counts?.needs_review,  "text-orange-200"],
          ["Rejected",     stats.counts?.invalid,       "text-red-200"],
        ].map(([label, n, cls]) => (
          <div key={label} className="bg-[#121815] border border-[#3A4640] rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">{label}</div>
            <div className={`text-2xl font-heading font-bold ${cls}`} data-testid={`hp-stat-${label.toLowerCase().replace(" ", "-")}`}>{n || 0}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          ["unprocessed", "Unprocessed"],
          ["mine",        "My In Progress"],
          ["completed",   "Completed"],
          ["review",      "Needs Manager Review"],
          ["invalid",     "Rejected"],
          ["all",         "All"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            data-testid={`hp-tab-${key}`}
            className={`text-xs uppercase font-heading tracking-wider px-3 py-1.5 rounded-md border transition ${
              tab === key
                ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]"
                : "bg-[#121815] text-[#8A8F98] border-[#3A4640] hover:text-[#F8F9FA]"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-2 top-2.5 text-[#8A8F98]" />
          <Input placeholder="Search name, mobile, village, batch code…"
                 value={q} onChange={e => setQ(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && refresh()}
                 className="pl-7 bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]"
                 data-testid="hp-search-input" />
        </div>
        <Input placeholder="District" value={filters.district}
               onChange={e => setFilters(f => ({ ...f, district: e.target.value }))}
               className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]"
               data-testid="hp-filter-district" />
        <Input placeholder="Block" value={filters.block}
               onChange={e => setFilters(f => ({ ...f, block: e.target.value }))}
               className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]"
               data-testid="hp-filter-block" />
        <Input placeholder="Village" value={filters.village}
               onChange={e => setFilters(f => ({ ...f, village: e.target.value }))}
               className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]"
               data-testid="hp-filter-village" />
      </div>

      {/* Table */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] px-4 py-2 border-b border-[#3A4640]">
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Mobile</div>
          <div className="col-span-3">Location</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-[#8A8F98]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-[#8A8F98]" data-testid="hp-empty">
            No records in this view.
          </div>
        ) : items.map((it) => {
          const f = it.fields || {};
          return (
            <div key={it.id}
              data-testid={`hp-row-${it.id}`}
              className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#3A4640] last:border-b-0 hover:bg-[#1A1F1C] cursor-pointer"
              onClick={() => nav(`/admin/historical-processing/${it.id}`)}>
              <div className="col-span-3 min-w-0">
                <div className="font-medium text-[#F8F9FA] truncate">{f.name || <span className="text-[#8A8F98]">— no name —</span>}</div>
                {f.fathers_name && <div className="text-xs text-[#8A8F98] truncate">S/o {f.fathers_name}</div>}
              </div>
              <div className="col-span-2 font-mono text-sm text-[#F8F9FA]">{f.phone || <span className="text-[#8A8F98]">—</span>}</div>
              <div className="col-span-3 text-xs text-[#8A8F98] truncate">
                {[f.village, f.gram_panchayat, f.block, f.district].filter(Boolean).join(" · ") || <span>—</span>}
              </div>
              <div className="col-span-2 text-xs text-[#8A8F98] truncate">
                <span className="font-mono">{it.batch_code}</span>
                {it.source?.sheet && <span> · {it.source.sheet}</span>}
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${STATUS_CHIP[it.processing_status] || ""}`}
                  data-testid={`hp-status-${it.id}`}>
                  {it.processing_status}
                </span>
              </div>
              <div className="col-span-1 text-right">
                <ChevronRight size={16} className="inline text-[#8A8F98]" />
              </div>
            </div>
          );
        })}
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#3A4640] text-xs text-[#8A8F98]">
            <div>Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
                data-testid="hp-prev-btn">
                <ChevronLeft size={14} /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}
                data-testid="hp-next-btn">
                Next <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {canReview && (
        <div className="mt-6 text-xs text-[#8A8F98]">
          <b>Manager tip:</b> Records tagged <i>Needs Manager Review</i> can be
          reopened via the record page.
        </div>
      )}
    </div>
  );
}

// ============================================================
// Processing screen — /admin/historical-processing/:id
// ============================================================
export function HistoricalProcessingRecord() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);

  const canReview = user?.role === "admin" || (user?.permissions || []).includes("historical.review");
  const canProcess = user?.role === "admin" || (user?.permissions || []).includes("historical.process");

  async function load() {
    try {
      const { data } = await api.get(`/historical-customers/${id}`);
      setRec(data);
      setFields(data.fields || {});
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/admin/historical-processing");
    }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (!rec) return <div className="p-6 text-[#8A8F98]">Loading…</div>;

  const isMine = rec.claimed_by && user?.id === rec.claimed_by;
  const canEdit = (rec.processing_status === "IN_PROGRESS" && isMine) || user?.role === "admin";
  const isProcessed = rec.processing_status === "PROCESSED";

  async function doClaim() {
    setBusy(true);
    try {
      const { data } = await api.post(`/historical-customers/${id}/claim`);
      setRec({ ...data.record, duplicate_candidates: rec.duplicate_candidates });
      toast.success("Record claimed — you can now edit and complete it");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function saveEdits() {
    setBusy(true);
    try {
      await api.patch(`/historical-customers/${id}`, { fields });
      toast.success("Draft saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function refreshDupes() {
    try {
      const { data } = await api.post(`/historical-customers/${id}/duplicate-check`);
      setRec(r => ({ ...r, duplicate_candidates: data.candidates || [] }));
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function completeAsNew() {
    if (!fields.name) { toast.error("Customer Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/historical-customers/${id}/complete`, {
        action: "create_new", fields,
      });
      toast.success(`Customer created — you may return to the queue`);
      nav(`/customers/${data.customer_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function linkExisting(customerId) {
    if (!window.confirm("Link this historical record to the selected existing customer?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/historical-customers/${id}/complete`, {
        action: "link_existing", matched_customer_id: customerId, fields,
      });
      toast.success("Linked to existing customer");
      nav(`/customers/${data.customer_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function needsReview() {
    const reason = window.prompt("Why does this record need manager review?");
    if (!reason || reason.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/historical-customers/${id}/needs-review`, { reason });
      toast.success("Sent to manager review");
      nav("/admin/historical-processing");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function rejectRow() {
    const reason = window.prompt("Reason for rejecting this historical record:");
    if (!reason || reason.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/historical-customers/${id}/reject`, { reason });
      toast.success("Record marked invalid");
      nav("/admin/historical-processing");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function managerReturn() {
    setBusy(true);
    try {
      await api.post(`/historical-customers/${id}/manager-return`, { reason: "Returned to queue" });
      toast.success("Returned to unprocessed queue");
      nav("/admin/historical-processing");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  function set(k, v) { setFields(f => ({ ...f, [k]: v })); }

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="historical-processing-record">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin/historical-processing")}
                data-testid="hp-back-btn">
          <ChevronLeft size={16} /> Back to queue
        </Button>
        <span className={`text-[10px] uppercase font-heading tracking-wider px-2 py-1 rounded border ${STATUS_CHIP[rec.processing_status] || ""}`}
              data-testid="hp-record-status">
          {rec.processing_status}
        </span>
        {rec.processing_status === "PROCESSED" && rec.processed_customer_id && (
          <a href={`/customers/${rec.processed_customer_id}`}
             className="text-xs text-[#39FF14] underline">
            View created customer →
          </a>
        )}
      </div>

      {/* Source strip */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 mb-4 text-xs text-[#8A8F98] grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><b className="text-[#F8F9FA]">Batch:</b> {rec.batch_code}</div>
        <div><b className="text-[#F8F9FA]">Sheet/Row:</b> {rec.source?.sheet} · {rec.source?.row}</div>
        <div><b className="text-[#F8F9FA]">Imported:</b> {formatDate(rec.source?.imported_at)}</div>
        <div><b className="text-[#F8F9FA]">By:</b> {rec.source?.imported_by_name}</div>
      </div>

      {/* Claim banner */}
      {rec.processing_status === "UNPROCESSED" && (
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="text-sm text-[#F8F9FA]">This record is unclaimed. Click below to start processing.</div>
          <Button className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C]" onClick={doClaim} disabled={busy || !canProcess}
                  data-testid="hp-claim-btn">
            <PlayCircle size={14} className="mr-1" /> Start Processing
          </Button>
        </div>
      )}
      {rec.processing_status === "IN_PROGRESS" && !isMine && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4 text-sm text-yellow-200 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5" />
          Locked by <b className="mx-1">{rec.claimed_by_name || "another staff member"}</b> — you cannot edit until they release it{user?.role === "admin" && " (admin can force-release)"}.
        </div>
      )}
      {rec.processing_status === "NEEDS_REVIEW" && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4 text-sm text-orange-200 flex items-start justify-between gap-2">
          <div><b>Needs Manager Review.</b> Reason: {rec.review_reason || "—"}</div>
          {canReview && (
            <Button size="sm" variant="outline" onClick={managerReturn} disabled={busy}
                    data-testid="hp-manager-return-btn">
              <Undo2 size={14} className="mr-1" /> Return to queue
            </Button>
          )}
        </div>
      )}

      {/* Form */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 mb-4">
        <div className="text-sm font-heading uppercase tracking-wider text-[#8A8F98] mb-3">Customer Information</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["name", "Customer Name *"],
            ["phone", "Mobile"],
            ["alternate_phone", "Alternate Mobile"],
            ["email", "Email"],
            ["fathers_name", "Father's / Husband's Name"],
            ["address", "Address"],
            ["village", "Village / Locality"],
            ["gram_panchayat", "Gram Panchayat"],
            ["block", "Block / Tehsil"],
            ["district", "District"],
            ["state", "State"],
            ["pin_code", "PIN Code"],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs text-[#8A8F98]">{label}</Label>
              <Input value={fields[k] || ""} onChange={e => set(k, e.target.value)}
                     disabled={!canEdit || isProcessed}
                     className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA] mt-1"
                     data-testid={`hp-field-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-xs text-[#8A8F98]">Historical Notes</Label>
          <textarea value={fields.historical_notes || ""} onChange={e => set("historical_notes", e.target.value)}
                    disabled={!canEdit || isProcessed} rows={3}
                    className="mt-1 w-full rounded-md bg-[#0B0F0C] border border-[#3A4640] text-[#F8F9FA] px-3 py-2 text-sm"
                    data-testid="hp-field-historical_notes" />
        </div>

        {canEdit && !isProcessed && (
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={saveEdits} disabled={busy} data-testid="hp-save-btn">
              Save Draft
            </Button>
          </div>
        )}
      </div>

      {/* Equipment section */}
      {(rec.equipment || []).length > 0 && (
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 mb-4">
          <div className="text-sm font-heading uppercase tracking-wider text-[#8A8F98] mb-3">Historical Equipment</div>
          {(rec.equipment || []).map((eq, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[#F8F9FA] border border-[#3A4640] rounded p-2 mb-2">
              <div><b className="text-[#8A8F98]">Category:</b> {eq.category || "—"}</div>
              <div><b className="text-[#8A8F98]">Brand:</b> {eq.brand || "—"}</div>
              <div><b className="text-[#8A8F98]">Model:</b> {eq.model || "—"}</div>
              <div><b className="text-[#8A8F98]">Serial:</b> {eq.serial_number || "—"}</div>
              <div><b className="text-[#8A8F98]">Engine:</b> {eq.engine_number || "—"}</div>
              <div><b className="text-[#8A8F98]">Chassis:</b> {eq.chassis_number || "—"}</div>
              <div><b className="text-[#8A8F98]">Invoice:</b> {eq.invoice_no || "—"}</div>
              <div><b className="text-[#8A8F98]">Date:</b> {eq.historical_date || "—"}</div>
            </div>
          ))}
        </div>
      )}

      {/* Duplicates */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-heading uppercase tracking-wider text-[#8A8F98]">Possible Duplicate Customers</div>
          <Button size="sm" variant="outline" onClick={refreshDupes} disabled={busy} data-testid="hp-dupes-refresh">
            <RefreshCw size={12} className="mr-1" /> Re-check
          </Button>
        </div>
        {(rec.duplicate_candidates || []).length === 0 ? (
          <div className="text-xs text-[#8A8F98]">No likely duplicates found.</div>
        ) : (rec.duplicate_candidates || []).map((c) => (
          <div key={c.id} className="flex items-center justify-between border border-[#3A4640] rounded p-2 mb-2">
            <div className="min-w-0">
              <div className="text-sm text-[#F8F9FA] truncate">
                <b>{c.name || c.organization_name || c.department_name}</b>
                <span className="ml-2 font-mono text-xs text-[#39FF14]">{c.tzat_no}</span>
              </div>
              <div className="text-xs text-[#8A8F98] truncate">
                {c.phone} · {[c.village, c.block, c.district].filter(Boolean).join(" · ")}
                {c.reasons?.length ? <span className="ml-2 text-yellow-200">({c.reasons.join(", ")})</span> : null}
              </div>
            </div>
            {canEdit && !isProcessed && (
              <Button size="sm" variant="outline" onClick={() => linkExisting(c.id)} disabled={busy}
                      data-testid={`hp-link-existing-${c.id}`}>
                <UserCheck size={12} className="mr-1" /> Link to this customer
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Action buttons */}
      {canEdit && !isProcessed && (
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={rejectRow} disabled={busy}
                  className="border-red-500/40 text-red-200 hover:bg-red-500/10"
                  data-testid="hp-reject-btn">
            <XCircle size={14} className="mr-1" /> Reject
          </Button>
          <Button variant="outline" onClick={needsReview} disabled={busy}
                  className="border-orange-500/40 text-orange-200 hover:bg-orange-500/10"
                  data-testid="hp-needs-review-btn">
            <AlertTriangle size={14} className="mr-1" /> Needs Manager Review
          </Button>
          <Button className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C]" onClick={completeAsNew} disabled={busy}
                  data-testid="hp-complete-btn">
            <UserCheck size={14} className="mr-1" /> Complete &amp; Create Customer
          </Button>
        </div>
      )}

      {/* Review history */}
      {(rec.review_history || []).length > 0 && (
        <div className="mt-4 text-xs text-[#8A8F98]">
          <b>History:</b>
          <ul className="mt-1 space-y-1">
            {(rec.review_history || []).map((h, i) => (
              <li key={i}>
                {formatDate(h.at)} · <b className="text-[#F8F9FA]">{h.by_name}</b> · {h.action}
                {h.reason ? ` — ${h.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
